import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  Input,
  InputNumber,
  Radio,
  Result,
  Skeleton,
  Switch,
  message,
} from "antd";
import { CheckOutlined } from "@ant-design/icons";
import { LateralModal } from "@/components/modals/LateralModal";
import { getErrorMessage } from "@/utilities";
import { CuerpoConPie } from "./ui";

export interface GatewayPanelProps {
  sellerCode: string;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

interface Props<T> {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  title: string;
  width?: number;
  load: () => Promise<T>;
  save: (values: T) => Promise<unknown>;
  savedMessage: string;
  /** Los campos del formulario: se guardan con el Guardar del pie. */
  children: React.ReactNode;
  /** Lo que va debajo del formulario y se guarda solo, como las cuotas de Fiserv. */
  after?: React.ReactNode;
}

/**
 * El panel de una pasarela (xcons-docs#636). Arma el formulario recién con la configuración
 * cargada: si no carga, no hay formulario ni Guardar, porque guardar uno vacío pisaría las
 * credenciales. Mismo criterio que Configuración del seller.
 */
export function GatewayPanel<T extends object>({
  open,
  onClose,
  title,
  width,
  ...body
}: Props<T>) {
  return (
    <LateralModal
      hideTrigger
      openDrawer={open}
      closeDrawer={onClose}
      title={title}
      width={width}
    >
      <GatewayBody {...body} onClose={onClose} />
    </LateralModal>
  );
}

/**
 * Se monta cada vez que se abre el panel (`LateralModal` lo destruye al cerrar), así que el
 * formulario arranca siempre de lo guardado.
 */
function GatewayBody<T extends object>({
  onClose,
  onSaved,
  load,
  save,
  savedMessage,
  children,
  after,
}: Omit<Props<T>, "open" | "title" | "width"> & { closeModal?: () => void }) {
  const [form] = Form.useForm<T>();
  const [data, setData] = useState<T | null>(null);
  const [loadError, setLoadError] = useState<"forbidden" | "error" | null>(
    null,
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    load()
      .then(setData)
      .catch((e: any) =>
        setLoadError(e?.response?.status === 403 ? "forbidden" : "error"),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      await save(values);
      message.success(savedMessage);
      onSaved();
      onClose();
    } catch (err: any) {
      if (err?.errorFields) return; // validación del form: ya se ve en los campos
      message.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <CuerpoConPie
      pie={
        loadError ? (
          <Button onClick={onClose}>Cerrar</Button>
        ) : (
          <>
            <Button onClick={onClose}>Cancelar</Button>
            <Button
              type="primary"
              icon={<CheckOutlined />}
              iconPosition="end"
              loading={saving}
              disabled={!data}
              onClick={handleSave}
            >
              Guardar
            </Button>
          </>
        )
      }
    >
      {loadError ? (
        <Result
          status={loadError === "forbidden" ? "403" : "error"}
          title={
            loadError === "forbidden"
              ? "No tenés permisos para ver esta configuración"
              : "No se pudo cargar la configuración"
          }
          subTitle={
            loadError === "forbidden"
              ? "Pedile a un administrador del seller el permiso de configuración."
              : "Volvé a intentar en un momento."
          }
        />
      ) : !data ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : (
        <>
          <Form
            form={form}
            layout="vertical"
            initialValues={data}
            disabled={saving}
          >
            {children}
          </Form>
          {after}
        </>
      )}
    </CuerpoConPie>
  );
}

/** Estado, título y descripción: los tres primeros campos de toda pasarela. */
export const CamposComunes: React.FC = () => (
  <>
    <Form.Item label="Estado" name="enabled" valuePropName="checked">
      <Switch />
    </Form.Item>
    <Form.Item label="Título" name="title">
      <Input placeholder="El nombre que ve el cliente en el checkout" />
    </Form.Item>
    <Form.Item label="Descripción" name="description">
      <Input placeholder="Texto corto debajo del título" />
    </Form.Item>
  </>
);

/** Producción o Prueba, en vez de un switch suelto de "Modo producción". */
export const CampoModo: React.FC = () => (
  <Form.Item
    label="Modo"
    name="productionModeEnabled"
    extra="En Prueba los pagos no se cobran de verdad."
  >
    <Radio.Group
      optionType="button"
      options={[
        { label: "Producción", value: true },
        { label: "Prueba", value: false },
      ]}
    />
  </Form.Item>
);

export const CampoMontoMaximo: React.FC = () => (
  <Form.Item
    label="Monto máximo sin verificación"
    name="maxAmountWithoutVerification"
  >
    <InputNumber min={0} prefix="$" style={{ width: "100%" }} />
  </Form.Item>
);
